import React, { Component } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { userActions } from '../../webapi';
import { usersUpdate } from '../../actions';
import { Button, OverlayPage, OverlayPageContents, OverlayPageSection, OverlayPageBottomButtons, CheckBox, Text } from '../../components';

import { TEXT_LIGHT, COLOUR_TEAL, COLOUR_RED, COLOUR_BRANDING_ACTION, hexToRGBAstring } from '../../js';

// The bulkUserScheduler runs on cron(0/10 * * * ? *) — every 10 minutes on the UTC 10-minute marks.
const IMPORT_INTERVAL_MINUTES = 10;
const REFRESH_INTERVAL_MS = 30000;

class BulkUploads extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      data: [],
      chosenFile: null,
      PageError: null,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadBulks(true);
    // Auto-refresh so statuses track the background scheduler without a manual reload.
    this.refreshInterval = setInterval(() => this.loadBulks(false), REFRESH_INTERVAL_MS);
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  loadBulks = (isInitial = false) => {
    if (isInitial) this.setState({ loading: true });

    userActions
      .getbulks(this.props.auth.site)
      .then((res) => {
        if (!this._isMounted) return;
        const items = _.orderBy(res.data.bulkList.Items || [], ['CreatedUnix'], ['desc']);
        this.setState((prev) => {
          // Keep the open detail view pointed at the freshest copy so its statuses update live.
          const chosenFile = prev.chosenFile
            ? _.find(items, (i) => i.RowId === prev.chosenFile.RowId) || prev.chosenFile
            : null;
          return { loading: false, data: items, chosenFile, PageError: null };
        });
      })
      .catch((error) => {
        if (!this._isMounted) return;
        console.log(error);
        this.setState({ loading: false, PageError: 'Something went wrong. Please try again.' });
      });
  };

  // Human-readable name of the site being monitored. `auth.site` is the key;
  // siteInfo.siteName is the display name shown across the platform.
  currentSiteName = () => {
    const { auth } = this.props;
    const roles = auth.user && auth.user.Roles ? auth.user.Roles : [];
    const role = _.find(roles, (r) => r.site === auth.site);
    return role && role.siteInfo && !_.isEmpty(role.siteInfo.siteName) ? role.siteInfo.siteName : auth.site;
  };

  // Next scheduled scheduler run — the next UTC 10-minute mark, shown in local time.
  nextRun = () => {
    const nowUtc = moment.utc();
    const minutesPast = nowUtc.minute() % IMPORT_INTERVAL_MINUTES;
    return nowUtc
      .clone()
      .subtract(minutesPast, 'minutes')
      .startOf('minute')
      .add(IMPORT_INTERVAL_MINUTES, 'minutes')
      .local();
  };

  calloutStyle = () => ({
    backgroundColor: hexToRGBAstring(COLOUR_BRANDING_ACTION, 0.1),
    borderLeft: `3px solid ${COLOUR_BRANDING_ACTION}`,
    borderRadius: 5,
    padding: '12px 16px',
    marginBottom: 24,
  });

  getColour(ev) {
    if (ev.JobStatus === 'Complete') {
      return COLOUR_TEAL;
    }
    return TEXT_LIGHT;
  }

  renderFiles() {
    if (this.state.loading && _.isEmpty(this.state.data)) {
      return (
        <Text type="help" className="marginTop-24">
          Loading uploads…
        </Text>
      );
    }
    if (_.isEmpty(this.state.data)) {
      return (
        <Text type="help" className="marginTop-24">
          No uploads yet. Imported user lists will appear here.
        </Text>
      );
    }
    return (
      <div className="marginTop-24">
        {this.state.data.map((ev, index) => {
          const isSelected = this.state.chosenFile != null && this.state.chosenFile.RowId === ev.RowId;
          return (
            <div
              key={ev.RowId || index}
              onClick={() => this.setState({ chosenFile: ev })}
              className="pointer genericBoxShadow padding-8 flex flex-center flex-between marginBottom-8"
            >
              <div className="flex flex-center">
                <CheckBox isActive={isSelected} onChange={() => {}} style={{ marginRight: 14, marginBottom: 0 }} />
                <div className="fontRegular" style={{ minWidth: 230 }}>
                  Uploaded {moment.unix(ev.CreatedUnix).local().format('h:mm a, Do MMM YY')}
                </div>
                <div className="fontRegular" style={{ minWidth: 130 }}>
                  Users to upload: {ev.Users.length}
                </div>
                <div className="fontRegular">Send welcome: {ev.SendWelcome ? 'Yes' : 'No'}</div>
              </div>
              <div className="fontHeavy" style={{ marginRight: 8, color: this.getColour(ev) }}>
                {ev.JobStatus}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderFileDetails() {
    if (this.state.chosenFile == null) return null;
    return (
      <div className="padding-60 paddingVertical-40">
        <Text type="formTitleSmall" className="marginBottom-16">
          Upload results
        </Text>
        <div>
          {this.state.chosenFile.Users.map((ev, index) => {
            const status = _.isUndefined(ev.Success) ? 'Not started' : ev.Success ? 'Success' : 'Fail';
            const colour = _.isUndefined(ev.Success) ? TEXT_LIGHT : ev.Success ? COLOUR_TEAL : COLOUR_RED;
            return (
              <div key={index} className="genericBoxShadow padding-8 flex flex-center marginBottom-5">
                <div className="fontRegular" style={{ minWidth: 200 }}>
                  {ev.Name ? ev.Name.substring(0, 30) : ev.Email || ev.Phone}
                </div>
                <div className="fontHeavy" style={{ minWidth: 100, color: colour }}>
                  {status}
                </div>
                <div className="fontRegular">{ev.Message}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderError() {
    if (!this.state.PageError) return null;
    return (
      <Text type="error" className="marginTop-16">
        {this.state.PageError}
      </Text>
    );
  }

  renderPage() {
    const next = this.nextRun();
    return (
      <div>
        <div className="padding-60 paddingVertical-40 bottomDivideBorder">
          <Text type="formTitleLarge" className="marginBottom-16">
            Bulk upload status
          </Text>

          <div style={this.calloutStyle()}>
            <Text type="help" className="marginBottom-4">
              Monitoring uploads for
            </Text>
            <Text type="formTitleSmall">{this.currentSiteName()}</Text>
          </div>

          <Text type="help" className="marginBottom-8">
            Imports run automatically every {IMPORT_INTERVAL_MINUTES} minutes — next run {next.fromNow()} (around{' '}
            {next.format('h:mm a')}). This page refreshes every 30 seconds.
          </Text>

          {this.renderError()}
          {this.renderFiles()}
        </div>
        {this.renderFileDetails()}
      </div>
    );
  }

  render() {
    return (
      <OverlayPage>
        <OverlayPageContents>
          <OverlayPageSection className="pageSectionWrapper--newPopup">{this.renderPage()}</OverlayPageSection>
        </OverlayPageContents>
        <OverlayPageBottomButtons>
          <Button inline buttonType="tertiary" onClick={() => window.history.back()} isActive>
            Back
          </Button>
        </OverlayPageBottomButtons>
      </OverlayPage>
    );
  }
}

const mapStateToProps = (state) => {
  const { auth } = state;
  return { auth };
};

export default connect(mapStateToProps, { usersUpdate })(withRouter(BulkUploads));
